import { BookingStatus, getStatusLabelId } from 'store/bookings/types';
import { REJECTED_STATUS_LABEL_ID, isRejectedStatus } from '../rejection.util';

export type StatusPillVariant = 'positive' | 'pending' | 'negative';

// The legacy tile hides the pill entirely for ACCEPTED bookings and otherwise just shows the raw
// translated status word. The redesign always shows a pill, and — per the mockup — relabels the
// common ACCEPTED case as "Reserved" rather than "Accepted".
// RES-1174: both rejection statuses collapse to one tenant-facing label, for the same reason
// ACCEPTED is relabelled above -- MAP_STATUSES names the internal enum variant, and "Denied
// Available" is not something a tenant can read. The map itself is left alone because the landing
// carousel also reads it and does not surface rejections.
/*
 * RES-1367: CHECKED IN IS A REFINEMENT OF RESERVED, and so it is said in the same place.
 *
 * It used to be a block of its own in the tile's footer, under a divider -- a second status for the
 * same booking, in a second place, saying something the pill beside the title was already most of
 * the way to saying. Activity center states it in the pill, and a tenant reading both surfaces
 * should not have to learn two shapes for one fact.
 *
 * ⚠️ ONLY over ACCEPTED. `check_in.status` is the backend's and outlives the booking it belongs to:
 * a cancelled booking can still carry a closed check-in, and "Checked In" over Cancelled would be
 * this pill contradicting itself. Everything that is not a live, confirmed booking keeps the label
 * its status earned.
 *
 * Returns `undefined` for a status with no word -- the caller must then render no pill at all.
 */
export const getStatusPillLabelId = (status: BookingStatus | undefined, isCheckedIn = false): string | undefined => {
  // CONFIRMED sits with ACCEPTED: helix draws the line at payment, but the tile is telling the
  // tenant whether they have the room.
  if (status === BookingStatus.ACCEPTED || status === BookingStatus.CONFIRMED) {
    return isCheckedIn ? 'is_checked_in' : 'reserved';
  }

  return isRejectedStatus(status) ? REJECTED_STATUS_LABEL_ID : getStatusLabelId(status);
};

export const getStatusPillVariant = (status: BookingStatus | undefined): StatusPillVariant => {
  switch (status) {
    case BookingStatus.ACCEPTED:
    case BookingStatus.CONFIRMED:
      return 'positive';
    case BookingStatus.PENDING:
    case BookingStatus.PENDING_PAYMENT:
      return 'pending';
    default:
      // Everything else -- DENIED_*, CANCELED, LATE_CANCEL, NO_SHOW, and any status not in the enum.
      return 'negative';
  }
};
